// /app/frontend/src/pages/seo/AtlantaBlackDatingPage.jsx
// City landing page — Atlanta Black dating community.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  buildBreadcrumbSchema,
  GradientHero,
  FeatureCard,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

export default function AtlantaBlackDatingPage() {
  const path = '/atlanta-black-dating';
  const schemas = [
    organizationSchema,
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'Atlanta Black Dating', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Atlanta Black Dating Community"
        description="UnityLove connects Black singles, couples, and families in Atlanta seeking meaningful relationships, intentional connection, and a culture-first community."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="Atlanta, Georgia"
        headline="Meaningful Dating In Atlanta"
        sub="The Black mecca deserves a dating community that matches its energy — culture-first, intentional, and built for the long game."
        body="From Cascade to Buckhead, the West End to East Atlanta — UnityLove connects singles, couples, and families across the city."
        primaryCta={{ label: 'Create Your Profile Free', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'Browse All Guides', to: '/guides' }}
      />

      <main className="max-w-4xl mx-auto px-4 py-14">
        {/* Why Atlanta */}
        <section className="mb-12">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-4">
            Why Atlanta
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              Atlanta is one of the most influential Black cultural hubs in the country — a city shaped by HBCUs, by hip-hop, by civil-rights history, by Black wealth-building, and by some of the most beautiful Black love stories in America. The city has long set the cultural tone for Black America, and the dating scene reflects that: ambitious, intergenerational, faith-aware, and rooted in family.
            </p>
            <p>
              UnityLove is built for the version of Atlanta dating that actually matches the city. Not casual swiping at brunch. Real, intentional connection between people who came here — or were raised here — building something that lasts.
            </p>
          </div>
        </section>

        {/* Who's here */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6 text-center">
            Who You&rsquo;ll Find In Atlanta
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <FeatureCard
              icon="🎓"
              accent="amber"
              title="HBCU & AUC Alumni"
              body="Spelman, Morehouse, Clark Atlanta, Morris Brown — a city stacked with educated Black professionals who built lifelong networks during college."
            />
            <FeatureCard
              icon="💼"
              accent="emerald"
              title="Black Professionals & Entrepreneurs"
              body="A community of business owners, creatives, executives, and builders — many of whom relocated to Atlanta specifically for the Black professional ecosystem."
            />
            <FeatureCard
              icon="🏡"
              accent="rose"
              title="Couples & Families Building Together"
              body="From Stone Mountain to Smyrna, families putting down roots — and couples exploring poly, sister-wife, and plural family structures with intention."
            />
            <FeatureCard
              icon="⛪"
              accent="violet"
              title="Faith-Rooted Community"
              body="From traditional Black churches to Afrocentric spiritual practices, faith plays a central role in much of Atlanta&rsquo;s dating culture. UnityLove welcomes the full spectrum."
            />
          </div>
        </section>

        {/* Around the city */}
        <section className="mb-14 bg-slate-50 rounded-3xl p-6 sm:p-8">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
            Around The City
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4">
            Atlanta is a city of neighborhoods, and each one has its own dating energy:
          </p>
          <ul className="grid gap-3 sm:grid-cols-2 text-slate-700">
            <li><strong className="text-slate-900">West End / Cascade</strong> — Cultural anchor, family neighborhoods, Black-owned restaurants</li>
            <li><strong className="text-slate-900">Buckhead / Midtown</strong> — Date-night dining, after-work drinks, professionals</li>
            <li><strong className="text-slate-900">East Atlanta / Edgewood</strong> — Live music, art scene, younger crowd</li>
            <li><strong className="text-slate-900">South DeKalb / Stone Mountain</strong> — Established Black families, faith communities</li>
            <li><strong className="text-slate-900">Castleberry Hill / Old Fourth Ward</strong> — Creative scene, BeltLine walking dates</li>
            <li><strong className="text-slate-900">College Park / East Point</strong> — Growing community, family-oriented</li>
          </ul>
        </section>

        {/* What's different here */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">
            What Makes Atlanta Dating Different
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              People often say Atlanta dating is hard — that the dating pool feels saturated, that intentions are hard to read, that long-term-minded singles have to wade through a lot of noise. The honest truth is that the city has volume, and a platform built around volume (swipe culture) is the worst possible filter for the city&rsquo;s most thoughtful daters.
            </p>
            <p>
              UnityLove is a different filter. Intentional pacing. Verified profiles. Profile prompts that signal long-term intent. Themed lounges where the conversation actually goes somewhere. The city has the people; it just needed a different way to find them.
            </p>
          </div>
        </section>

        {/* Internal links */}
        <section className="mb-4">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">
            Continue Reading
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/black-love-dating-app" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Black Love Dating App</p>
              <p className="text-sm text-slate-600 mt-1">Centering culture and connection.</p>
            </a>
            <a href="/houston-black-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Houston Black Dating</p>
              <p className="text-sm text-slate-600 mt-1">Connections in H-Town.</p>
            </a>
            <a href="/black-sister-wife-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Black Sister Wife Dating</p>
              <p className="text-sm text-slate-600 mt-1">A community for plural family-building.</p>
            </a>
            <a href="/dating-app-for-intentional-relationships" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Dating App For Intentional Relationships</p>
              <p className="text-sm text-slate-600 mt-1">For people looking for more than casual.</p>
            </a>
          </div>
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA />
    </div>
  );
}
